/**
 * How many tracker requests a drawer Refresh costs — an honest ESTIMATE.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The cost is dominated by DATE WINDOWS: the backend fetches one report set per
 * distinct cube key, so two formula groups over "last 7 days" cost what one
 * does, while adding a custom range adds a whole set.
 *
 * Mirrors, deliberately and by hand, three backend files (the frontend cannot
 * import them):
 *   voluum/services/flow-optimizer/runner/build-metrics-cube.ts  (buildFetchSpecs)
 *   voluum/services/flow-optimizer/evaluator/cube-lookup-key.ts  (the key rules)
 *   voluum/services/flow-optimizer/evaluator/compare-to-ranges.ts (leaf compareTo)
 *   flow-optimizer-queue/metrics/rotation-metrics.types.ts       (the `judges` gate)
 * Same precedent as utils/default-rule-date-range.ts. If those move, this drifts
 * — which is what the spec test is for.
 *
 * Per window: Voluum 4 calls (path/lander/offer reports + the composite cube),
 * Binom 5 (its cube service says so in as many words). Verified 2026-08-17.
 * Tres Commas re-lists the Voluum report/flow classes, so it costs what Voluum
 * does — minus the queue judge, which only runs for a tracker with a queue.
 */

import {
  ALL_LAYERS,
  type TrackerId,
  type CapacityRule,
  type ExcludeRulesTree,
  type FlowLayer,
  type FlowOptimizerConfig,
  type FormulaGroup,
  type RuleNode,
} from '@trafficpulse/shared';

import { queueProviderOf } from '@/features/trackers/flow-optimizer/queue-provider';

import { ROTATION_ACTIONS, collectRuleWindowKeys } from './estimate-window-keys';

const DEFAULT_KEY = 'last-7-days';

/** Report calls per distinct date window, per tracker. */
const PER_WINDOW: Readonly<Record<TrackerId, number>> = { voluum: 4, binom: 5, 'tres-commas': 4 };

/** Flow/structure reads that happen regardless of the windows. */
const FLOW_READS: Readonly<Record<TrackerId, number>> = { voluum: 3, binom: 2, 'tres-commas': 3 };

/** Reads that are not reliably a cache hit (Binom's flow read). */
const MIN_READS: Readonly<Record<TrackerId, number>> = { voluum: 0, binom: 2, 'tres-commas': 0 };

export type CostProvider = TrackerId;

export interface RefreshCost {
  /** Distinct date windows the config implies. */
  windows: number;
  /** Cold cache — the realistic ceiling. */
  max: number;
  /** Everything cached (the usual case within 60s) — the floor. */
  min: number;
  /** Extra calls one more NEW date range would add. */
  perExtraWindow: number;
}

const groupKey = (g: FormulaGroup): string =>
  g.dateRange === 'custom' ? `custom:${g.id}` : g.dateRange;

const appliesTo = (g: FormulaGroup, layer: FlowLayer): boolean =>
  !g.appliesTo?.length || g.appliesTo.includes(layer);

/** True when some ruled layer has no applicable formula group. */
function needsDefault(rules: ExcludeRulesTree, groups: FormulaGroup[]): boolean {
  return ALL_LAYERS.some(
    (layer) => Boolean(rules[layer]) && !groups.some((g) => appliesTo(g, layer)),
  );
}

/** A rotation-action group with NO window of its own still makes the queue
 *  judge, so this walks for the ACTIONS, not for window keys. */
function hasAction(node: RuleNode): boolean {
  if (node.kind !== 'group') return false;
  if (node.actions?.some((a) => ROTATION_ACTIONS.includes(a))) return true;
  return (node.children ?? []).some(hasAction);
}

const hasRotationRule = (rules: ExcludeRulesTree): boolean =>
  ALL_LAYERS.some((l) => {
    const tree = rules[l];
    return Boolean(tree) && hasAction(tree as RuleNode);
  });

const hasCap = (rules: CapacityRule[] | undefined): boolean =>
  (rules ?? []).some((r) => r.max !== null && r.max !== undefined);

/** Distinct windows for one of the two backend paths. */
function windowKeys(
  config: FlowOptimizerConfig,
  rotationOnly: boolean,
): Set<string> {
  const groups = config.formula?.groups ?? [];
  const keys = new Set<string>(groups.map(groupKey));
  const rules = config.excludeRules ?? {};
  for (const layer of ALL_LAYERS) {
    const tree = rules[layer];
    if (tree) collectRuleWindowKeys(tree, rotationOnly, keys);
  }
  if (rotationOnly || needsDefault(rules, groups)) keys.add(DEFAULT_KEY);
  return keys;
}

/**
 * What one Refresh will cost. `tagWindows` is the number of DISTINCT extra
 * windows the flow tag rules add — the caller knows the tag rules, this file
 * deliberately does not (they are a global list, not part of the config).
 */
export function estimateRefreshCost(input: {
  config: FlowOptimizerConfig;
  provider: CostProvider;
  tagWindows?: number;
}): RefreshCost {
  const { config, provider } = input;
  const weights = windowKeys(config, false);
  const judges =
    queueProviderOf(provider) !== null &&
    (hasRotationRule(config.excludeRules ?? {}) || hasCap(config.capacityRules));
  if (judges) for (const k of windowKeys(config, true)) weights.add(k);
  const windows = weights.size + Math.max(0, input.tagWindows ?? 0);
  const per = PER_WINDOW[provider];
  return {
    windows,
    max: windows * per + FLOW_READS[provider],
    // Reports are cached 60s and shared with the scheduled run; the flow read
    // is the only leg that is not reliably a hit.
    min: MIN_READS[provider],
    perExtraWindow: per,
  };
}
